import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useDropzone } from 'react-dropzone';
import { documentsAPI } from '../../services/api';
import { useConfig } from '../../context/ConfigContext';
import { useUpload } from '../../context/UploadContext';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  CalendarIcon,
  FunnelIcon,
  PlusIcon,
  TrashIcon,
  EllipsisHorizontalIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

// PDF Icon component matching Figma design
const PDFIcon = ({ className }) => (
  <div className={`flex items-center justify-center w-12 h-12 bg-red-500 rounded-lg ${className}`}>
    <span className="text-white text-xs font-bold">PDF</span>
  </div>
);

// File type icon based on extension
const FileTypeIcon = ({ fileType }) => {
  const colors = {
    PDF: { bg: 'bg-red-500', text: 'PDF' },
    CSV: { bg: 'bg-green-500', text: 'CSV' },
    Excel: { bg: 'bg-blue-500', text: 'XLS' },
    Text: { bg: 'bg-gray-500', text: 'TXT' },
    URL: { bg: 'bg-purple-500', text: 'URL' },
  };
  const config = colors[fileType] || { bg: 'bg-gray-500', text: 'DOC' };

  return (
    <div className={`flex items-center justify-center w-12 h-12 ${config.bg} rounded-lg flex-shrink-0`}>
      <span className="text-white text-xs font-bold">{config.text}</span>
    </div>
  );
};

// Document/website ingest status badge — color + label + tooltip per status.
// "ingested_with_warnings" is the document-service signal that part of the
// smart-tagging pass didn't complete; the document is still usable but
// some retrievals may be slightly less precise. It is NOT an error.
//
// Tooltip uses a custom popover (not the native `title` attribute) because
// `title` has a 1-2s hover delay, doesn't fire on touch, and can be
// suppressed by some browser extensions.
const StatusBadge = ({ status }) => {
  const [open, setOpen] = useState(false);
  const map = {
    completed: {
      label: 'Completed',
      classes: 'border-green-300 text-green-600',
      tooltip: '',
    },
    processing: {
      label: 'Processing',
      classes: 'border-yellow-300 text-yellow-600',
      tooltip: '',
    },
    ingested_with_warnings: {
      label: 'Ingested with warnings',
      classes: 'border-yellow-300 text-yellow-700 bg-yellow-50',
      tooltip:
        "Some parts of this document didn't get the full smart-tagging pass, so a few search results from it may be a little less precise. The document is still fully usable — you can ask the assistant about it.",
    },
  };
  const meta = map[status] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    classes: 'border-red-300 text-red-600',
    tooltip: '',
  };

  if (!meta.tooltip) {
    return (
      <span className={`px-3 py-1 border rounded text-xs font-medium ${meta.classes}`}>
        {meta.label}
      </span>
    );
  }

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`px-3 py-1 border rounded text-xs font-medium cursor-help ${meta.classes}`}
      >
        {meta.label}
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-50 right-0 bottom-full mb-2 w-72 bg-gray-900 text-white text-xs leading-relaxed rounded-lg px-3 py-2 shadow-lg"
        >
          {meta.tooltip}
        </span>
      )}
    </span>
  );
};

// Image extraction warnings — surfaces per-image issues the vision
// extraction pass hit while ingesting a document (e.g. an unreadable
// figure or a page image that timed out). Non-blocking: the document is
// still ingested normally, this is just detail for troubleshooting.
// Renders nothing when there are no warnings.
const ImageExtractionWarnings = ({ warnings }) => {
  const [open, setOpen] = useState(false);

  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 px-2 py-0.5 border border-amber-200 bg-amber-50 text-amber-700 rounded text-xs font-medium hover:bg-amber-100 transition-colors"
      >
        {warnings.length} image extraction warning{warnings.length > 1 ? 's' : ''}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {warnings.map((w, i) => (
            <li key={i}>
              <span className="font-semibold">{w.kind}</span>
              {w.location ? ` — ${w.location}` : ''}
              {w.detail ? `: ${w.detail}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function AdminDocuments() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [activeTab, setActiveTab] = useState('documents');
  const [urlInput, setUrlInput] = useState('');
  const [isIngesting, setIsIngesting] = useState(false);
  const fileInputRef = useRef(null);
  const { config } = useConfig();
  const { uploadingFiles, uploadFile } = useUpload();
  const queryClient = useQueryClient();

  const [hasProcessing, setHasProcessing] = useState(false);

  const { data: documents, isLoading, error } = useQuery('documents', documentsAPI.getDocuments, {
    refetchInterval: hasProcessing ? 3000 : false,
  });

  const { data: outdatedData } = useQuery('outdated-documents', documentsAPI.getOutdatedDocuments);

  useEffect(() => {
    const processing = documents?.documents?.some(doc => doc.status === 'processing');
    setHasProcessing(!!processing);
  }, [documents]);

  const deleteMutation = useMutation(
    (documentId) => documentsAPI.deleteDocument(documentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('documents');
        toast.success('Document deleted successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Delete failed');
      },
    }
  );

  const ingestUrlMutation = useMutation(
    (url) => documentsAPI.ingestUrl(url),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('documents');
        toast.success('Website ingestion started!');
        setUrlInput('');
        setIsIngesting(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to ingest URL');
        setIsIngesting(false);
      },
    }
  );

  const refreshMutation = useMutation(
    (documentId) => documentsAPI.refreshDocument(documentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('documents');
        toast.success('Website refresh started!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Refresh failed');
      },
    }
  );

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      uploadFile(file, config.max_file_size_mb * 1024 * 1024);
    });
  }, [config.max_file_size_mb, uploadFile]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
    },
    maxSize: config.max_file_size_mb * 1024 * 1024,
    noClick: true,
    noKeyboard: true,
  });

  const handleDelete = (documentId, filename) => {
    if (window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      deleteMutation.mutate(documentId);
    }
  };

  const handleIngestUrl = (e) => {
    e.preventDefault();
    if (!urlInput.trim()) return;
    setIsIngesting(true);
    ingestUrlMutation.mutate(urlInput.trim());
  };

  const toggleSelectDoc = (docId) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const truncateUrl = (url, maxLen = 60) => {
    if (url.length <= maxLen) return url;
    return url.substring(0, maxLen) + '...';
  };

  // Split documents into documents and websites
  const allDocs = documents?.documents || [];
  const filteredDocuments = allDocs.filter(doc => {
    if (doc.file_type === 'URL') return false;
    if (!searchQuery) return true;
    return doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
  });
  const filteredWebsites = allDocs.filter(doc => {
    if (doc.file_type !== 'URL') return false;
    if (!searchQuery) return true;
    return doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const documentCount = allDocs.filter(d => d.file_type !== 'URL').length;
  const websiteCount = allDocs.filter(d => d.file_type === 'URL').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cleona-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 font-source">Error loading documents: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-source">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-arsenal">Resources</h1>
          <p className="mt-1 text-gray-600">
            Upload and manage your knowledge base documents. {documents?.total || 0} of {config.max_documents} documents used.
          </p>
        </div>
        {activeTab === 'documents' && (
          <button
            onClick={open}
            className="flex items-center gap-2 px-5 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium"
          >
            <PlusIcon className="h-5 w-5" />
            Upload Documents
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('documents')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'documents'
                ? 'border-cleona-600 text-cleona-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Documents ({documentCount})
          </button>
          <button
            onClick={() => setActiveTab('websites')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'websites'
                ? 'border-cleona-600 text-cleona-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Websites ({websiteCount})
          </button>
        </nav>
      </div>

      {/* Outdated Documents Banner */}
      {outdatedData?.outdated_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <ArrowPathIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-medium">{outdatedData.outdated_count} {outdatedData.outdated_count === 1 ? 'document was' : 'documents were'}</span> processed with an older version. Re-upload for improved results.
          </p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={activeTab === 'documents' ? 'Search Documents here' : 'Search Websites here'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent text-sm"
          />
        </div>
        <div className="relative">
          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="appearance-none pl-10 pr-10 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent text-sm text-gray-600 bg-white min-w-[160px]"
          >
            <option value="">Select Date</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
        </button>
        <button className="px-6 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium">
          Search
        </button>
      </div>

      {/* ===== DOCUMENTS TAB ===== */}
      {activeTab === 'documents' && (
        <>
          {/* Upload Progress */}
          {uploadingFiles.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Uploading...</h3>
              {uploadingFiles.map((upload) => (
                <div key={upload.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <FileTypeIcon fileType="PDF" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{upload.file.name}</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-cleona-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Document Count */}
          <p className="text-sm text-gray-600">
            Showing: <span className="font-semibold text-gray-900">{filteredDocuments.length} {filteredDocuments.length === 1 ? 'Document' : 'Documents'}</span>
          </p>

          {/* Document List */}
          <div {...getRootProps()} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <input {...getInputProps()} />

            {isDragActive && (
              <div className="absolute inset-0 bg-cleona-50 bg-opacity-90 flex items-center justify-center z-10 rounded-xl border-2 border-dashed border-cleona-400">
                <p className="text-cleona-600 font-medium">Drop files here to upload...</p>
              </div>
            )}

            {filteredDocuments.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {filteredDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSelectDoc(doc.id)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                        selectedDocs.has(doc.id)
                          ? 'bg-cleona-600 border-cleona-600'
                          : 'border-gray-300 hover:border-cleona-400'
                      }`}
                    >
                      {selectedDocs.has(doc.id) && (
                        <CheckIcon className="h-4 w-4 text-white" />
                      )}
                    </button>

                    {/* File Icon */}
                    <FileTypeIcon fileType={doc.file_type} />

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {doc.filename}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {formatFileSize(doc.file_size)} • {doc.chunk_count} chunks • {formatDate(doc.created_at)}
                      </p>
                      {doc.error_message && (
                        <p className="text-xs text-red-600 mt-1">{doc.error_message}</p>
                      )}
                      <ImageExtractionWarnings warnings={doc.meta_data?.image_extraction_warnings} />
                    </div>

                    {/* Type Badge */}
                    <span className="px-3 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700">
                      {doc.file_type}
                    </span>

                    {/* Status Badge */}
                    <StatusBadge status={doc.status} />

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(doc.id, doc.filename)}
                      disabled={deleteMutation.isLoading}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>

                    {/* More Menu */}
                    <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                      <EllipsisHorizontalIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-16 text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-900 font-medium">No documents uploaded yet</p>
                <p className="text-sm text-gray-500 mt-1">Upload your first document to get started.</p>
                <button
                  onClick={open}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors text-sm font-medium"
                >
                  <PlusIcon className="h-4 w-4" />
                  Upload Document
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== WEBSITES TAB ===== */}
      {activeTab === 'websites' && (
        <>
          {/* URL Input Card */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <form onSubmit={handleIngestUrl} className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg flex-shrink-0">
                <GlobeAltIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <input
                  type="url"
                  placeholder="Enter a website URL (e.g., https://example.com/page)"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent text-sm"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isIngesting || !urlInput.trim()}
                className="flex items-center gap-2 px-5 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isIngesting ? (
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                ) : (
                  <PlusIcon className="h-5 w-5" />
                )}
                Add Website
              </button>
            </form>
            <p className="mt-2 text-xs text-gray-500 ml-14">
              The page will be rendered with a browser and its text content extracted into your knowledge base.
            </p>
          </div>

          {/* Website Count */}
          <p className="text-sm text-gray-600">
            Showing: <span className="font-semibold text-gray-900">{filteredWebsites.length} {filteredWebsites.length === 1 ? 'Website' : 'Websites'}</span>
          </p>

          {/* Website List */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {filteredWebsites.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {filteredWebsites.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    {/* URL Icon */}
                    <FileTypeIcon fileType="URL" />

                    {/* URL Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate" title={doc.filename}>
                        {truncateUrl(doc.filename)}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {doc.chunk_count} chunks • {formatDate(doc.created_at)}
                      </p>
                      {doc.error_message && (
                        <p className="text-xs text-red-600 mt-1">{doc.error_message}</p>
                      )}
                      <ImageExtractionWarnings warnings={doc.meta_data?.image_extraction_warnings} />
                    </div>

                    {/* Status Badge */}
                    <StatusBadge status={doc.status} />

                    {/* Refresh Button */}
                    <button
                      onClick={() => refreshMutation.mutate(doc.id)}
                      disabled={doc.status === 'processing' || refreshMutation.isLoading}
                      className="p-2 text-gray-500 hover:text-cleona-600 hover:bg-cleona-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Re-crawl website"
                    >
                      <ArrowPathIcon className={`h-5 w-5 ${doc.status === 'processing' ? 'animate-spin' : ''}`} />
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(doc.id, doc.filename)}
                      disabled={deleteMutation.isLoading}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-16 text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <GlobeAltIcon className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-900 font-medium">No websites added yet</p>
                <p className="text-sm text-gray-500 mt-1">Add a website URL above to ingest its content into your knowledge base.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
